import React, { useState, useEffect, useMemo } from 'react'
import {
  FormControlLabel,
  Grid,
  InputLabel,
  Radio,
  RadioGroup,
  Checkbox,
  FormGroup,
  Typography,
  TextField,
  Select,
  MenuItem,
} from '@mui/material'
import { StyledCard } from '../StyledCard'

export const HRQuestions = ({
  handleHRQuestions,
  setHasUnansweredRequiredQuestions = () => {},
  fields,
  hideShadow,
}: any) => {
  // Derive questions from fields using useMemo
  const questions = useMemo(
    () =>
      fields.map((field: any) => ({
        id: field._id,
        text: field.questions[0].questionText,
        type: field.questions[0].questionType,
        options: field.questions[0].options,
        isRequired: field.questions[0].isRequired,
        isVisible: field.questions[0].isVisible,
      })),
    [fields]
  )

  // Update the type definition for answers
  const [answers, setAnswers] = useState<Record<any, any>>(() =>
    questions.reduce(
      (acc: any, question: any) => ({
        ...acc,
        [question.id]: question.type === 'Multiple Choice' ? [] : '',
      }),
      {}
    )
  )

  // Track required questions
  const requiredQuestions = useMemo(
    () => questions.filter((q: any) => q.isRequired).map((q: any) => q.id),
    [questions]
  )

  // Check if all required questions are answered
  const checkRequiredQuestions = useMemo(() => {
    return requiredQuestions.every((id: string) => {
      const question = questions.find((q: any) => q.id === id)
      if (!question?.isVisible) return true // Skip invisible questions
      const answer = answers[id]
      return answer && !(Array.isArray(answer) && answer.length === 0)
    })
  }, [requiredQuestions, answers, questions])

  // Handle Input Change
  const onInputChange = (questionId: string, value: string | any) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }))
  }

  // Transform data for API
  const transformedData = useMemo(
    () =>
      questions.map((question: any) => ({
        questionId: question.id,
        question: question.text,
        questionType: question.type,
        answerText:
          question.type === 'Yes/No' || question.type === 'shortAnswer'
            ? answers[question.id]
            : Array.isArray(answers[question.id])
            ? answers[question.id].join(', ')
            : '',
      })),
    [questions, answers]
  )

  // Update parent component about unanswered required questions
  useEffect(() => {
    setHasUnansweredRequiredQuestions(!checkRequiredQuestions)
  }, [checkRequiredQuestions, setHasUnansweredRequiredQuestions])

  // Use Effect
  useEffect(() => {
    handleHRQuestions(transformedData)
  }, [transformedData, handleHRQuestions])

  // Custom View
  const CustomView = hideShadow ? Grid : StyledCard

  return (
    <CustomView>
      <Typography variant='h6' gutterBottom>
        HR Questions
      </Typography>
      {questions.map((question: any) =>
        question.isVisible ? (
          <div key={question.id}>
            <InputLabel
              sx={{
                mt: 1,
                display: 'block',
                fontWeight: 'bold',
                color:
                  question.isRequired && !answers[question.id]
                    ? 'error.main'
                    : 'inherit',
              }}>
              {question.text}
              {question.isRequired && (
                <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
              )}
            </InputLabel>
            {question.type === 'Select' ? (
              <Select
                value={answers[question.id] || ''}
                onChange={e =>
                  onInputChange(question.id, e.target.value as string)
                }
                displayEmpty
                fullWidth>
                <MenuItem value=''>
                  <em>None</em>
                </MenuItem>
                {question.options?.map((option: string, optionKey: number) => (
                  <MenuItem key={optionKey} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            ) : question.type === 'Yes/No' ? (
              <RadioGroup
                aria-label={question.text}
                name={question.id}
                value={answers[question.id]}
                row
                onChange={e => onInputChange(question.id, e.target.value)}>
                <FormControlLabel
                  value='true'
                  control={<Radio />}
                  label='Yes'
                />
                <FormControlLabel
                  value='false'
                  control={<Radio />}
                  label='No'
                />
              </RadioGroup>
            ) : question.type === 'shortAnswer' ? (
              <TextField
                fullWidth
                variant='outlined'
                value={answers[question.id]}
                onChange={e => onInputChange(question.id, e.target.value)}
              />
            ) : (
              <FormGroup>
                {question.options?.map((option: string, optionKey: number) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={(answers[question.id] as string[]).includes(
                          option
                        )}
                        onChange={e => {
                          const currentAnswers = answers[
                            question.id
                          ] as string[]
                          const newValue: any = e.target.checked
                            ? [...currentAnswers, option]
                            : currentAnswers.filter((o: string) => o !== option)
                          onInputChange(question.id, newValue)
                        }}
                      />
                    }
                    label={option}
                    key={optionKey}
                  />
                ))}
              </FormGroup>
            )}
          </div>
        ) : null
      )}
      {!checkRequiredQuestions && (
        <Typography color='error' sx={{ mt: 2 }}>
          Please answer all required questions.
        </Typography>
      )}
    </CustomView>
  )
}
